import { ComponentType } from 'react'

// This file will be populated as we migrate pages
// For now, we'll create placeholder components and add real ones as we migrate

/**
 * Route Registry
 * Maps database route paths to React components
 * This must include ALL possible routes that can come from the database
 */
// Import migrated pages
import Login from '@/pages/Login'
import Dashboard from '@/pages/Dashboard'
import ForgotPassword from '@/pages/ForgotPassword'
import ResetPassword from '@/pages/ResetPassword'
import TwoFactor from '@/pages/TwoFactor'
import TwoFactorSetup from '@/pages/TwoFactor/Setup'
import Profile from '@/pages/Profile'
import TestCSPPage from '@/pages/TestCSP'
import TaskList from '@/pages/Helpdesk/TaskList'
import IncomingRequest from '@/pages/IncomingRequest'
import Acknowledged from '@/pages/Helpdesk/Acknowledged'
import ParentTicketList from '@/pages/Helpdesk/ParentTicketList'
import ParentTicketCreationPage from '@/pages/Helpdesk/ParentTicketCreation'
import ParentTicketsListPage from '@/pages/ParentTickets/index'
import ParentTicketCreatePage from '@/pages/ParentTickets/Create'
import ParentTicketEditPage from '@/pages/ParentTickets/Edit'
import ParentTicketViewPage from '@/pages/ParentTickets/View'
import ServiceTypesListPage from '@/pages/ServiceTypes/index'
import ServiceTypeCreatePage from '@/pages/ServiceTypes/Create'
import ServiceTypeEditPage from '@/pages/ServiceTypes/Edit'
import ParentTicketStatusesListPage from '@/pages/ParentTicketStatuses/index'
import ParentTicketStatusCreatePage from '@/pages/ParentTicketStatuses/Create'
import ParentTicketStatusEditPage from '@/pages/ParentTicketStatuses/Edit'
import AgentAvailabilityStatusesListPage from '@/pages/AgentAvailabilityStatuses/index'
import AgentAvailabilityStatusCreatePage from '@/pages/AgentAvailabilityStatuses/Create'
import AgentAvailabilityStatusEditPage from '@/pages/AgentAvailabilityStatuses/Edit'
import ChildTicketStatusesListPage from '@/pages/ChildTicketStatuses/index'
import ChildTicketStatusCreatePage from '@/pages/ChildTicketStatuses/Create'
import ChildTicketStatusEditPage from '@/pages/ChildTicketStatuses/Edit'
import SlaTimingListPage from '@/pages/SlaTiming/index'
import SlaTimingCreatePage from '@/pages/SlaTiming/Create'
import SlaTimingEditPage from '@/pages/SlaTiming/Edit'
import SupportTypesListPage from '@/pages/SupportTypes/index'
import SupportTypeCreatePage from '@/pages/SupportTypes/Create'
import SupportTypeEditPage from '@/pages/SupportTypes/Edit'
import TaskTemplatesListPage from '@/pages/TaskTemplates/index'
import TaskTemplateCreatePage from '@/pages/TaskTemplates/Create'
import TaskTemplateEditPage from '@/pages/TaskTemplates/Edit'
import TaskFieldModulesPage from '@/pages/TaskFieldModules/index'
import AgentStatusPage from '@/pages/AgentStatus/index'
import MailboxInboxPage from '@/pages/MailboxInbox'
import CaseThreadsPage from '@/pages/CaseThreads'
import ReportsOverviewPage from '@/pages/Reports/Overview'
import SlaCompliancePage from '@/pages/Reports/SlaCompliance'
import AgentPerformancePage from '@/pages/Reports/AgentPerformance'
import FeAttendancePage from '@/pages/Reports/FeAttendance'
import TicketVolumePage from '@/pages/Reports/TicketVolume'
import HistoricalExportPage from '@/pages/Reports/HistoricalExport'
import SdKpiReportPage from '@/pages/Reports/SdKpi'
import FeKpiReportPage from '@/pages/Reports/FeKpi'
import AprDurationsReportPage from '@/pages/Reports/AprDurations'
import TaskReportsPage from '@/pages/Reports/TaskReports'
import TicketProductivityPage from '@/pages/Reports/TicketProductivity'
import ContactCenterProductivityPage from '@/pages/Reports/ContactCenterProductivity'
import AgentProductivityPage from '@/pages/Reports/AgentProductivity'
import CustomerSatisfactionPage from '@/pages/CustomerSatisfaction'
import IssueCategoriesListPage from '@/pages/IssueCategories/index'
import IssueCategoryCreatePage from '@/pages/IssueCategories/Create'
import IssueCategoryEditPage from '@/pages/IssueCategories/Edit'
import TicketOptionListsPage from '@/pages/TicketOptionLists/index'
import ToolPartCatalogPage from '@/pages/ToolPartCatalog/index'
import FeChecklistsPage from '@/pages/ServiceCatalog/FeChecklists'
import ChecklistTemplatesPage from '@/pages/ServiceCatalog/ChecklistTemplates'
import FESchedulePage from '@/pages/FieldEngineers/Schedule'
import FELiveMapPage from '@/pages/FieldEngineers/LiveMap'
import MyTasksPage from '@/pages/MyTasks/index'
import InboxPage from '@/pages/Helpdesk/Inbox'
import UserManualPage from '@/pages/Help/UserManual'
import FaqPage from '@/pages/Help/Faq'

export const routeRegistry: Record<string, ComponentType<any>> = {
  // Public routes
  '/login': Login,
  '/forgot-password': ForgotPassword,
  '/reset-password': ResetPassword,
  '/two-factor': TwoFactor,
  '/two-factor/setup': TwoFactorSetup,

  // Dashboard
  '/dashboard': Dashboard,
  // Help (pinned in sidebar; always available)
  '/user-manual': UserManualPage,
  '/faq': FaqPage,
  // Other
  '/profile': Profile,
  '/test-csp': TestCSPPage,

  // Helpdesk (dashboard uses main Dashboard with ticket report)
  '/incoming-request': IncomingRequest,
  '/helpdesk/dashboard': Dashboard,
  '/helpdesk/tasklist': TaskList,
  '/helpdesk/acknowledged': Acknowledged,
  '/helpdesk/parent-tickets': ParentTicketList,
  '/helpdesk/parent-ticket-create': ParentTicketCreationPage,
  '/helpdesk/parent-ticket-list': ParentTicketList,
  '/helpdesk/inbox': InboxPage,

  // Parent Tickets (canonical routes)
  '/tickets': ParentTicketsListPage,
  '/tickets/create': ParentTicketCreatePage,
  '/tickets/:id/edit': ParentTicketEditPage,
  '/tickets/:id/view': ParentTicketViewPage,
  '/tickets/:id': ParentTicketEditPage,

  // Service Catalog - Service Types (align with backend user_routes: list, :id = edit, create)
  '/service-catalog/service-types': ServiceTypesListPage,
  '/service-catalog/service-types/create': ServiceTypeCreatePage,
  '/service-catalog/service-types/:id': ServiceTypeEditPage,

  // Service Catalog - Parent Ticket Statuses
  '/service-catalog/parent-ticket-statuses': ParentTicketStatusesListPage,
  '/service-catalog/parent-ticket-statuses/create': ParentTicketStatusCreatePage,
  '/service-catalog/parent-ticket-statuses/:id': ParentTicketStatusEditPage,

  // Service Catalog - Agent Availability Statuses
  '/service-catalog/agent-availability-statuses': AgentAvailabilityStatusesListPage,
  '/service-catalog/agent-availability-statuses/create': AgentAvailabilityStatusCreatePage,
  '/service-catalog/agent-availability-statuses/:id': AgentAvailabilityStatusEditPage,

  // Service Catalog - Child Ticket Statuses
  '/service-catalog/child-ticket-statuses': ChildTicketStatusesListPage,
  '/service-catalog/child-ticket-statuses/create': ChildTicketStatusCreatePage,
  '/service-catalog/child-ticket-statuses/:id': ChildTicketStatusEditPage,

  // Service Catalog - SLA & Timing
  '/service-catalog/sla-timing': SlaTimingListPage,
  '/service-catalog/sla-timing/create': SlaTimingCreatePage,
  '/service-catalog/sla-timing/:id': SlaTimingEditPage,

  // Service Catalog - Support Types
  '/service-catalog/support-types': SupportTypesListPage,
  '/service-catalog/support-types/create': SupportTypeCreatePage,
  '/service-catalog/support-types/:id': SupportTypeEditPage,

  '/service-catalog/task-templates': TaskTemplatesListPage,
  '/service-catalog/task-templates/create': TaskTemplateCreatePage,
  '/service-catalog/task-templates/:id': TaskTemplateEditPage,
  '/service-catalog/task-field-modules': TaskFieldModulesPage,

  // Service Catalog - Customer Satisfaction
  '/service-catalog/customer-satisfaction': CustomerSatisfactionPage,

  // Agent Status (TL/STL: reports; Web Admin / Developer Account: assignable agents)
  '/agent-status': AgentStatusPage,

  // Microsoft Graph mailbox (ingested emails)
  '/mailbox-inbox': MailboxInboxPage,
  '/case-threads': CaseThreadsPage,

  // Reports
  '/reports/overview': ReportsOverviewPage,
  '/reports/analytics': ReportsOverviewPage,
  '/reports/sd-kpi': SdKpiReportPage,
  '/reports/fe-kpi': FeKpiReportPage,
  '/reports/apr-durations': AprDurationsReportPage,
  '/reports/sla-compliance': SlaCompliancePage,
  '/reports/agent-performance': AgentPerformancePage,
  '/reports/fe-attendance': FeAttendancePage,
  '/reports/ticket-volume': TicketVolumePage,
  '/reports/historical-export': HistoricalExportPage,
  '/reports/task-reports': TaskReportsPage,
  '/reports/ticket-productivity': TicketProductivityPage,
  '/reports/contact-center-productivity': ContactCenterProductivityPage,
  '/reports/agent-productivity': AgentProductivityPage,

  // Service Catalog - Issue Categories
  '/service-catalog/issue-categories': IssueCategoriesListPage,
  '/service-catalog/issue-categories/create': IssueCategoryCreatePage,
  '/service-catalog/issue-categories/:id': IssueCategoryEditPage,
  '/service-catalog/ticket-option-lists': TicketOptionListsPage,
  '/service-catalog/tool-part-catalog': ToolPartCatalogPage,
  '/service-catalog/fe-checklists': FeChecklistsPage,
  '/service-catalog/checklist-templates': ChecklistTemplatesPage,

  // Field Engineers
  '/field-engineers/schedule': FESchedulePage,
  '/field-engineers/live-map': FELiveMapPage,

  // My Tasks (cross-ticket personal task list)
  '/my-tasks': MyTasksPage,
}

/**
 * Get component for a route path
 * Handles dynamic segments (e.g., :id, :userId)
 */
export function getRouteComponent(path: string): ComponentType<any> | null {
  // Normalize: registry keys use leading slash and single slashes
  const normalized =
    '/' + (path || '').replace(/^\/+/, '').replace(/\/+/g, '/').replace(/\/+$/, '') || '/'

  // Direct match
  if (routeRegistry[normalized]) {
    return routeRegistry[normalized]
  }

  // Try to match dynamic routes
  // e.g., "/categories/123" matches "/categories/:id"
  const dynamicPatterns = Object.keys(routeRegistry).filter((key) =>
    key.includes(':'),
  )

  for (const pattern of dynamicPatterns) {
    const regex = new RegExp('^' + pattern.replace(/:[^/]+/g, '[^/]+') + '$')
    if (regex.test(normalized)) {
      return routeRegistry[pattern]
    }
  }

  return null
}
